/* eslint-disable max-lines */
"use client";
import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import PopOver from "../../../components/atoms/popOver";
import DomesticShippingDateAndSlotSelection from "../../../components/organisms/ShippingDateAndSlotSelection/DomesticShippingDateAndSlotSelection";
import DeliveryDateSelector from "../../../components/molecules/deliveryDateSelector";
import CustomImage from "../../../components/atoms/customImage";
import isEmpty from "lodash.isempty";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  requestToFetchBlockedDatesAction,
  requestToFetchDeliveryDatesAction,
  requestToFetchInternationalShippingMethod,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { WEEKDAYS, MONTH_NAMES, COUNTRIES, CURRENCY_ID } from "../../../constants/common/common";
import { CAKE_TYPE, constants, productTypes } from "../../../constants/pageConstants/pdpConstants";
import {
  calDiscountPercentage,
  debounce,
  formatClassNames,
  getProductDeliveryType,
  isSameDayDeliveryAvailable,
  pageName,
  updateCurrency,
} from "../../../utils/common/";
import RedirectionToPLPComponent from "./redirectionToPLPComponent";
import { fetchCalendarFooterMsg } from "../../../api/pdpApi";
import InternationalShippingDateAndSlotSelection from "../../../components/organisms/ShippingDateAndSlotSelection/InternationalShippingDateAndSlotSelection";
import { getOutOfStock, onAutoFillDateTimeSlots } from "../../../utils/pdp/nonDeliverable";
import { requestSurgePriceDetails, setDeliveryDateErrorFromAddToCart } from "../../../store/slices/pdpSlice";
import { cleverTapCommonEvents, cleverTapCommonWords, validationFor } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import SpecialDateCoupon from "./specialDateCoupon";
import { setMandateErrorAction } from "../../../store/slices/locationLock";
import { convertTo12Hour, dateToDMY, formatDateRange, getIsRakhiDelivery } from "../../../utils/common/deliveryDate";

/**
 * Renders a section with delivery date and time slot options.
 *
 * This component displays delivery date and time inputs based on the given product and catalog information.
 * It provides the full flow of selecting a delivery date and time slot, including appropriate input fields
 * and any associated UI elements. The UI adjusts based on whether the user is on a mobile device or not.
 *
 * @returns {JSX.Element} The rendered DeliveryDateAndTimeSlotSelection component.
 */
const DeliveryDateAndtimeSlotSelection = ({ currentItem, isMobile, selectedCurrency }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [isPopOverVisible, setIsPopOverVisible] = useState(false);
  const [msgContent, setMsgContent] = useState({});
  const [cutoffTimestamp, setCutoffTimestamp] = useState(false);
  const [isErrorMsgAnimate, setIsErrorMsgAnimate] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const pathname = usePathname();

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const {
    selectedIntLocation,
    selectedDomesticLocation,
    selectedDeliveryDetails,
    pinCode,
    address,
    deliveryDates,
    blockedDates,
    shippingDetailsInternational,
    deliveryDateError,
    updatedDeliveryDatesAndGeoPrice,
    mandateErrorClick,
    catalogId,
    shippingDetails,
    nextAvailableDifference,
    multiProductDateRange,
    clientVariantType,
    isCartVisible,
  } = useSelector(
    (state) => ({
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      selectedDomesticLocation: state.locationLock.selectedDomesticLocation,
      selectedDeliveryDetails: state?.shippingDateAndSlotSelection.selectedDeliveryDetails,
      pinCode: state?.locationLock?.selectedDomesticLocation?.pincode,
      address: state?.locationLock?.selectedDomesticLocation?.address,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      blockedDates: state?.shippingDateAndSlotSelection?.blockedDates,
      shippingDetailsInternational: state?.shippingDateAndSlotSelection?.internationalDeliveryMethods,
      deliveryDateError: state?.pdp?.deliveryDateError,
      updatedDeliveryDatesAndGeoPrice: state?.shippingDateAndSlotSelection?.updatedDeliveryDatesAndGeoPrice,
      mandateErrorClick: state?.locationLock?.mandateErrorClick,
      catalogId: state?.appConfig?.catalogId,
      shippingDetails: state?.shippingDateAndSlotSelection?.shippingDetails,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      multiProductDateRange: state?.shippingDateAndSlotSelection?.multiProductDateRange,
      clientVariantType: state?.pdp?.clientVariantType,
      isCartVisible: state?.cart?.isCartVisible,
    }),
    shallowEqual,
  );

  const isInternational = currentItem?.catalogId !== COUNTRIES.INDIA;
  const isOutOfStock = getOutOfStock(currentItem?.productStockAvailable, currentItem?.productId);
  const isRakhiDelivery = getIsRakhiDelivery(isInternational, currentItem?.urlIdentifier);
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;

  const discountPercentage = calDiscountPercentage(
    currentItem?.productPrice?.listPrice,
    currentItem?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
  const cleverTapData = {
    page_type: cleverTapCommonWords.PDP,
    page_name: currentItem?.urlIdentifier,
    product_name: currentItem?.productName,
    pid: currentItem?.productId,
    product_category: currentItem?.primaryCategoryId,
    product_price: currentItem?.productPrice?.price,
    is_variant_available: currentItem?.isVariant === "Y",
    is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false,
    delivery_type: productDeliveryType,
    is_international: isInternational,
    city: isInternational ? selectedIntLocation?.location : "",
    area: isInternational ? "" : address,
    pincode: isInternational ? "" : pinCode,
    system_pincode: isInternational ? "" : pinCode,
    country: currentItem?.catalogId,
    strike_price: currentItem?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: currentItem?.productReviewsAndRatings?.ratingValue,
    product_review_count: currentItem?.productReviewsAndRatings?.totalReview,
    earliest_delivery_date: isInternational ? "" : deliveryDates?.deliveryDates?.[0],
    clean_url: pathname,
    complete_url: completePathName,
  };

  const popupCleverTapData = {
    ...cleverTapData,
    date_of_delivery: selectedDeliveryDetails?.selectedDate || "",
    next_available_date_difference: isInternational ? "" : nextAvailableDifference?.nextAvailableDateDiff,
    delivery_shipping: selectedDeliveryDetails?.currentShippingMethod?.shippingMethodName || "",
    delivery_shipping_fee: selectedDeliveryDetails?.timeSlots?.timeSlots?.shippingPrice ?? 0,
    delivery_slot: selectedDeliveryDetails?.timeSlots?.formatTime || "",
    sku_id: currentItem?.skuCode,
  };

  const isInFocus = isInternational ? !!selectedIntLocation?.pincode : !!pinCode;

  const isDisabled = !(isInternational
    ? selectedIntLocation?.pincode
    : pinCode && deliveryDates?.deliveryDates?.length);

  /**
   * This method is used to get same day delivery data
   *
   */
  const getSameDayDeliveryData = async () => {
    try {
      const response = await fetchCalendarFooterMsg();
      setMsgContent(response);
      const availabilityStatus = isSameDayDeliveryAvailable(response?.cutoffTimestamp);
      setCutoffTimestamp(availabilityStatus);
    } catch (error) {
      setMsgContent("");
    }
  };

  useEffect(() => {
    if (
      shippingDetails?.data?.length > 0 &&
      pinCode &&
      !isCartVisible &&
      (shippingDetails?.selectedShippingMethod === productTypes.COURIER ||
        shippingDetails?.selectedShippingMethod === productTypes.FREE_SHIPPING)
    ) {
      onAutoFillDateTimeSlots(shippingDetails, deliveryDates, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingDetails?.data, pinCode, shippingDetails?.selectedShippingMethod]);

  useEffect(() => {
    if (mandateErrorClick) {
      setIsErrorMsgAnimate(true);
      setTimeout(() => {
        setIsErrorMsgAnimate(false);
        dispatch(setMandateErrorAction(false));
      }, 500);
    }
  }, [mandateErrorClick, dispatch]);
  useEffect(() => {
    if (
      blockedDates &&
      deliveryDates?.deliveryDates?.indexOf(dateToDMY(new Date())) === -1 &&
      !isInternational &&
      isEmpty(msgContent)
    ) {
      getSameDayDeliveryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedDates, deliveryDates?.deliveryDates]);

  useEffect(() => {
    if (deliveryDateError) {
      const cleverTapData = {
        page_name: pageName(isHome, isPLP, pathname, currentItem?.urlIdentifier),
        pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
        validation_text: constants.DELIVERY_DATE_AND_TIME_SLOT_VALIDATION,
        validation_for: validationFor.LOCATION_FIELD,
      };

      trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDateError]);

  useEffect(() => {
    // Renders only for digital products.
    if (
      !(currentItem?.shippingDisplayOptions && currentItem?.shippingDisplayOptions?.showPincodeOrCity) &&
      initialLoad
    ) {
      const { address, pincode } = selectedDomesticLocation;
      const { pincode: pincodeInt } = selectedIntLocation;
      let productIds = currentItem?.productId;

      if (address && pincode && catalogId === COUNTRIES.INDIA) {
        dispatch(
          requestToFetchDeliveryDatesAction({
            productId: productIds,
            pinCode: pincode,
            currencyUomId: CURRENCY_ID,
            catalogId: currentItem?.catalogId,
            countryGeoId: currentItem?.countryGeoId,
          }),
        );
        dispatch(
          requestToFetchBlockedDatesAction({
            productId: productIds,
            pinCode: pincode,
          }),
        );
      } else if (pincodeInt && catalogId !== COUNTRIES.INDIA) {
        dispatch(
          requestToFetchInternationalShippingMethod({
            productId: productIds,
            city: pincodeInt,
          }),
        );
      }
      setInitialLoad(false);
    }
  }, [selectedDomesticLocation, selectedIntLocation]);

  /**
   * Handles the click event for selecting delivery details.
   *
   * This function toggles the visibility of the delivery details drawer
   *
   * @function
   * @param {Object} value - The selected delivery details containing date, time slots, and shipping method.
   * @returns {void}
   */
  const onToggleDeliveryDetailsPopUp = () => {
    setIsPopOverVisible((e) => !e);
  };

  /**
   * Toggles the visibility of the delivery details drawer.
   *
   * @function
   * @returns {void}
   */
  const onHandlePopOverVisibility = debounce(() => {
    if (!isDisabled && !isPopOverVisible) {
      if (isInternational) {
        dispatch(requestSurgePriceDetails({ productId: currentItem?.productId }));

        // To avoid multiple API calls.
        if (isEmpty(shippingDetailsInternational?.shippingDetails)) {
          dispatch(
            requestToFetchInternationalShippingMethod({
              productId: currentItem?.productId,
              city: selectedIntLocation?.pincode,
            }),
          );
        }
      }
    }
    setIsPopOverVisible((e) => !e);
    trackEvent(cleverTapCommonEvents.deliveryPopupViewed, popupCleverTapData, ["clevertap"]);
  }, 100);

  /**
   * Renders the input value of delivery text field
   *
   * @function
   * @returns {void}
   */
  const renderDeliveryInputValue = () => {
    if (!isEmpty(selectedDeliveryDetails?.selectedDate)) {
      if (deliveryDateError) {
        dispatch(setDeliveryDateErrorFromAddToCart(false));
      }
      const startTime12Hour = convertTo12Hour(selectedDeliveryDetails.timeSlots.timeSlots.startTime);
      const endTime12Hour = convertTo12Hour(selectedDeliveryDetails.timeSlots.timeSlots.endTime);
      const price = selectedDeliveryDetails.timeSlots.timeSlots.shippingPrice;
      const dateString = selectedDeliveryDetails?.selectedDate;
      const [day, month, year] = (dateString ?? "").split("-");

      const date = new Date(year, month - 1, day);

      const weekday = WEEKDAYS[date.getDay()]?.slice(0, 3);
      const monthName = MONTH_NAMES[date.getMonth()]?.slice(0, 3);

      const formattedDate = currentItem?.isMultiOrderProduct
        ? formatDateRange(multiProductDateRange)
        : `${weekday}, ${day} ${monthName}`;

      return (
        <div className={`flex text-left text-sm font-500 text-black-100 ${isMobile ? "flex-col" : "flex-row gap-4"}`}>
          <span className={formatClassNames(isMobile && "flex-shrink-0")}>
            {`${formattedDate}, ${startTime12Hour} - ${endTime12Hour} ${isMobile ? "" : " -"}`}
          </span>
          <span className={formatClassNames(isMobile && "overflow-hidden truncate whitespace-nowrap")}>
            {`${selectedDeliveryDetails?.currentShippingMethod?.shippingMethodName} : `}
            {updateCurrency(selectedCurrency, price, true, !isInternational)}
          </span>
        </div>
      );
    }

    return <div className="text-sm font-500 text-grey-500">{constants.SELECT_DELIVERY_DATE_AND_TIME}</div>;
  };

  return (
    <div className="flex flex-col" id="datetimelink">
      {isInternational && (
        <DeliveryDateSelector
          onClickHandler={onHandlePopOverVisibility}
          value={renderDeliveryInputValue()}
          errorStyleType={"animate"}
          isDisabled={isDisabled}
          isInFocus={isInFocus && isEmpty(selectedDeliveryDetails?.selectedDate)}
          errorMsg={deliveryDateError ? constants.DELIVERY_DATE_AND_TIME_SLOT_VALIDATION : undefined}
          isErrorMsgAnimate={isErrorMsgAnimate}
          startAdornment={
            <CustomImage src={`/icons/calender-icon.svg`} width={24} height={24} alt="email icon" sizes="10vw" />
          }
          endAdornment={
            <CustomImage
              src={isInFocus ? "/icons/arrow-right-dark.svg" : "/icons/arrow-right-gray.svg"}
              width={16}
              height={16}
              alt="email icon"
              sizes="10vw"
            />
          }
        />
      )}
      {!isEmpty(updatedDeliveryDatesAndGeoPrice?.specialDateCouponDetails) && (
        <SpecialDateCoupon
          specialDateCouponDetails={updatedDeliveryDatesAndGeoPrice?.specialDateCouponDetails}
          isMobile={isMobile}
        />
      )}

      {isInternational ? (
        <PopOver
          classes={`${isMobile ? "w-[100%] h-[100%] rounded-none" : "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"}`}
          isVisible={isPopOverVisible}
          isBackdrop={true}
          id="multiProduct"
          border={false}
        >
          <InternationalShippingDateAndSlotSelection
            classes={isRakhiDelivery ? "md:w-[832px] md:h-[475px]" : "md:h-500 md:w-500"}
            currentItem={currentItem}
            onToggleDeliveryDetailsPopUp={onToggleDeliveryDetailsPopUp}
            isMobile={isMobile}
            selectedCurrency={selectedCurrency}
            popupCleverTapData={popupCleverTapData}
            isRakhiDelivery={isRakhiDelivery}
          />
        </PopOver>
      ) : (
        <DomesticShippingDateAndSlotSelection
          currentItem={currentItem}
          deliveryDates={deliveryDates}
          blockedDates={blockedDates}
          isCutoffTimestamp={cutoffTimestamp}
          setCutoffTimestamp={setCutoffTimestamp}
          msgContent={msgContent}
          pincode={pinCode || ""}
          isMobile={isMobile}
          selectedCurrency={selectedCurrency}
          popupCleverTapData={popupCleverTapData}
          cleverTapData={cleverTapData}
        />
      )}
      {currentItem?.catalogId === COUNTRIES.INDIA && !isOutOfStock && deliveryDates?.deliveryDates?.length > 0 && (
        <RedirectionToPLPComponent
          blockedDates={blockedDates?.deliveryBlockDates || ""}
          isDisplayCatalogContent={blockedDates?.displayCatalogContent || false}
          isCutoffTimestamp={cutoffTimestamp}
          isCalender={false}
          deliveryDatesList={deliveryDates?.deliveryDates}
          samedayMsgContent={msgContent}
          limitedCatalogContent={blockedDates?.content}
          pincode={pinCode || ""}
          cleverTapData={cleverTapData}
        />
      )}
    </div>
  );
};

DeliveryDateAndtimeSlotSelection.propTypes = {
  currentItem: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  selectedCurrency: PropTypes.object,
};

export default DeliveryDateAndtimeSlotSelection;
